import React, { useEffect, useState } from "react";
import { Button, Form, Alert } from "react-bootstrap";
import styled from "styled-components";
import { toast } from "react-toastify";
import {
  setExportPassphrase,
  getExportPassphraseStatus,
  generateExportToken,
  revokeExportToken,
  getExportTokenStatus,
  downloadMyExport,
} from "../services/exportService";
import { decryptExportFile } from "./utils/exportCrypto";
import Loader from "./common/loader";

const TokenBox = styled.pre`
  background-color: ${({ theme }) => theme.card};
  border: ${({ theme }) => theme.cardBorder};
  color: ${({ theme }) => theme.text};
  padding: 0.6rem;
  border-radius: 4px;
  word-break: break-all;
  white-space: pre-wrap;
  font-size: 0.85rem;
`;

const CodeBlock = styled.pre`
  background-color: ${({ theme }) => theme.card};
  border: ${({ theme }) => theme.cardBorder};
  color: ${({ theme }) => theme.text};
  padding: 0.6rem;
  border-radius: 4px;
  font-size: 0.8rem;
  overflow-x: auto;
`;

function saveBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function errorMessage(ex, fallback) {
  return (ex.response && ex.response.data) || fallback;
}

const ExportSettings = () => {
  const [passphraseStatus, setPassphraseStatus] = useState(null);
  const [tokenStatus, setTokenStatus] = useState(null);
  const [newToken, setNewToken] = useState(null);

  const [newPassphrase, setNewPassphrase] = useState("");
  const [confirmPassphrase, setConfirmPassphrase] = useState("");

  const [decryptPassphrase, setDecryptPassphrase] = useState("");

  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getExportPassphraseStatus()
      .then(({ data }) => setPassphraseStatus(data))
      .catch(() => setPassphraseStatus({ hasPassphrase: false }));
    getExportTokenStatus()
      .then(({ data }) => setTokenStatus(data))
      .catch(() => setTokenStatus({ hasToken: false, createdAt: null }));
  }, []);

  const handleSetPassphrase = async () => {
    if (newPassphrase.length < 8)
      return toast.error("Passphrase must be at least 8 characters.");
    if (newPassphrase !== confirmPassphrase)
      return toast.error("Passphrases don't match.");

    setBusy(true);
    try {
      await setExportPassphrase(newPassphrase);
      setPassphraseStatus({ hasPassphrase: true });
      setNewPassphrase("");
      setConfirmPassphrase("");
      toast.info(
        "Export passphrase set. Store it somewhere safe - it can't be recovered, and older backups won't decrypt with a new one."
      );
    } catch (ex) {
      toast.error(errorMessage(ex, "Could not set the export passphrase."));
    } finally {
      setBusy(false);
    }
  };

  const handleGenerateToken = async () => {
    setBusy(true);
    try {
      const { data } = await generateExportToken();
      setNewToken(data.token);
      setTokenStatus({ hasToken: true, createdAt: data.createdAt });
    } catch (ex) {
      toast.error(errorMessage(ex, "Could not generate an export token."));
    } finally {
      setBusy(false);
    }
  };

  const handleRevokeToken = async () => {
    setBusy(true);
    try {
      await revokeExportToken();
      setNewToken(null);
      setTokenStatus({ hasToken: false, createdAt: null });
      toast.info("Export token revoked.");
    } catch (ex) {
      toast.error(errorMessage(ex, "Could not revoke the export token."));
    } finally {
      setBusy(false);
    }
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      const response = await downloadMyExport();
      const filename = `aymanediary-export-${new Date()
        .toISOString()
        .slice(0, 10)}.enc`;
      saveBlob(
        new Blob([response.data], { type: "application/octet-stream" }),
        filename
      );
    } catch (ex) {
      toast.error(errorMessage(ex, "Download failed."));
    } finally {
      setBusy(false);
    }
  };

  const handleDecryptFile = async e => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!decryptPassphrase)
      return toast.error("Enter the passphrase this backup was encrypted with.");

    setBusy(true);
    try {
      const buffer = await file.arrayBuffer();
      const decrypted = await decryptExportFile(buffer, decryptPassphrase);
      saveBlob(
        new Blob([JSON.stringify(decrypted, null, 2)], {
          type: "application/json",
        }),
        "aymanediary-export-decrypted.json"
      );
    } catch (ex) {
      toast.error(
        "Could not decrypt that file - wrong passphrase, or it wasn't created by this account."
      );
    } finally {
      setBusy(false);
    }
  };

  if (!passphraseStatus || !tokenStatus) return <Loader fontSize="1.5rem" />;

  return (
    <React.Fragment>
      <h3 className="text-center mb-4">Export</h3>

      <p className="text-muted" style={{ fontSize: "0.9rem" }}>
        Every entry, encrypted (AES-256-GCM) with a passphrase you choose.
        The server keeps an encrypted copy so it can encrypt exports on its
        own - downloading here just uses your login, no extra secret needed.
        Decrypting later happens entirely offline, using the passphrase you
        remember, with no dependency on this backend staying up.
      </p>

      <h5 className="mt-4">1. Export passphrase</h5>
      <div className="mb-2">
        <strong>Status: </strong>
        {passphraseStatus.hasPassphrase ? "set" : "not set"}
      </div>
      <Form.Group>
        <Form.Control
          type="password"
          placeholder="New export passphrase (min 8 characters)"
          className="mb-2"
          value={newPassphrase}
          onChange={e => setNewPassphrase(e.target.value)}
        />
        <Form.Control
          type="password"
          placeholder="Confirm passphrase"
          className="mb-2"
          value={confirmPassphrase}
          onChange={e => setConfirmPassphrase(e.target.value)}
        />
      </Form.Group>
      <Button
        variant="primary"
        className="mb-4"
        disabled={busy}
        onClick={handleSetPassphrase}
      >
        {passphraseStatus.hasPassphrase
          ? "Change passphrase"
          : "Set passphrase"}
      </Button>
      {passphraseStatus.hasPassphrase && (
        <Alert variant="warning" className="mb-4">
          Changing your passphrase only affects future exports - backups you
          already downloaded still need the passphrase that was active when
          you downloaded them.
        </Alert>
      )}

      <h5>2. Download</h5>
      <p className="text-muted" style={{ fontSize: "0.85rem" }}>
        You're logged in, so this just works - no token needed.
      </p>
      <Button
        variant="outline-secondary"
        className="mb-4"
        disabled={busy}
        onClick={handleDownload}
      >
        <i className="fa fa-download mr-1" aria-hidden="true"></i>
        Download now
      </Button>

      <h5>3. Export token (for scripts only)</h5>
      <p className="text-muted" style={{ fontSize: "0.85rem" }}>
        Only needed for an unattended script pulling backups on a schedule -
        it lets that script authenticate without your real login. Not needed
        for downloading here in the browser.
      </p>
      <div className="mb-2">
        <strong>Status: </strong>
        {tokenStatus.hasToken ? "active" : "none yet"}
        {tokenStatus.createdAt && (
          <span className="text-muted">
            {" "}
            (created {new Date(tokenStatus.createdAt).toLocaleString()})
          </span>
        )}
      </div>

      {newToken && (
        <Alert variant="warning">
          Copy this now - it won't be shown again.
          <TokenBox className="mt-2 mb-0">{newToken}</TokenBox>
        </Alert>
      )}

      <div className="mb-4">
        <Button
          variant="primary"
          className="mr-2 mb-2"
          disabled={busy}
          onClick={handleGenerateToken}
        >
          {tokenStatus.hasToken ? "Regenerate token" : "Generate token"}
        </Button>
        {tokenStatus.hasToken && (
          <Button
            variant="danger"
            className="mb-2"
            disabled={busy}
            onClick={handleRevokeToken}
          >
            Revoke
          </Button>
        )}
      </div>

      <h5>4. Decrypt a backup file</h5>
      <p className="text-muted" style={{ fontSize: "0.85rem" }}>
        Runs entirely in your browser - the file never touches the server.
      </p>
      <Form.Group>
        <Form.Control
          type="password"
          placeholder="Passphrase this file was encrypted with"
          className="mb-2"
          value={decryptPassphrase}
          onChange={e => setDecryptPassphrase(e.target.value)}
        />
        <Form.Control
          type="file"
          accept=".enc"
          disabled={busy}
          onChange={handleDecryptFile}
        />
      </Form.Group>

      <p className="text-muted mt-4 mb-2" style={{ fontSize: "0.85rem" }}>
        For a script that downloads periodically (e.g. a cron job) - only the
        token goes in the script's config. Revoking it any time stops future
        downloads without touching your passphrase or password:
      </p>
      <CodeBlock>{`curl -H "x-export-token: YOUR_TOKEN" \\
     ${process.env.REACT_APP_API_URL}/export \\
  -o aymanediary-$(date +%F).enc`}</CodeBlock>
      <p className="text-muted mt-2" style={{ fontSize: "0.85rem" }}>
        To decrypt outside the browser (no network needed), use the CLI in
        the API repo: <code>node scripts/decryptExport.js file.enc</code>
      </p>
    </React.Fragment>
  );
};

export default ExportSettings;
